import { Stack } from '@mui/system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContactReferenceType, type ContactReferenceTypeValue } from '@/api/address-book/types';
import { _getBffContactsInfinite } from '@/api/address-book-bff/infinite-queries';
import { _getAdminCompaniesInfinite } from '@/api/deal/infinite-queries';
import { _getCompaniesInfinite } from '@/api/deal/queries';
import { invalidateDemandMutationViews } from '@/api/demand/cache-invalidation';
import {
  _createCompanyDemandEntry,
  _createDemandEntry,
  _updateDemandEntry,
} from '@/api/demand/mutations';
import { _getDemandEntry } from '@/api/demand/queries';
import {
  DEMAND_CONFIDENCE_OPTIONS,
  type DemandConfidenceOption,
  type DemandEntryListItem,
  type DemandEntryPayload,
  INTERESTED_PARTY_TYPE,
  type InterestedPartyType,
} from '@/api/demand/types';
import { _getAdminIntroducersInfinite } from '@/api/introducer-dashboard/infinite-queries';
import InfoIcon from '@/assets/svg/ui-elements/info-circle-outline-gray.svg';
import AlertCircleRedIcon from '@/assets/svg/ui-elements/info-circle-outline-orange.svg';
import {
  Button,
  Input,
  Modal,
  Select,
  Separator,
  TextArea,
  theme,
  Tooltip,
  Typography,
} from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { AddContactModal } from '@/pages/address-book/modules/add-contact-modal-v3';
import { useCreatedContact } from '@/pages/address-book/modules/add-contact-modal-v3/use-created-contact';
import { SearchSelect, type SearchSelectOption } from '@/pages/deals/shared/search-select';
import { PagePaths } from '@/routes/page-paths';
import { showToast } from '@/shared/components/notification-popup';
import { useInfiniteScroll } from '@/shared/hooks/use-infinite-scroll';
import { Permission, useCan } from '@/shared/modules/permissions';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { formatISOAsDayMonYearDashes } from '@/utils/date';
import { onlyNumbers } from '@/utils/numbers/helpers';

import {
  ContactEmpty,
  CreateLink,
  Field,
  FooterRow,
  FormStack,
  LabelRow,
  RemovedBanner,
  RequiredMark,
  SelectRaisedBg,
  SuffixField,
  SuffixText,
} from './styled';

export type DemandEntryModalProps = {
  mode: 'add' | 'edit' | 'view';
  entry?: DemandEntryListItem;
  initialCompany?: { id: string; name: string };
  allowViewEdit?: boolean;
};

const NOTES_MAX_LENGTH = 2000;

const TITLES: Record<DemandEntryModalProps['mode'], string> = {
  add: 'Add Demand Entry',
  edit: 'Edit Demand Entry',
  view: 'View Demand Entry',
};

const SUBTITLES: Record<DemandEntryModalProps['mode'], string> = {
  add: '',
  edit: 'Update the details for this entry.',
  view: '',
};

type CreatedContact = { id: string; name: string; type: InterestedPartyType };

const toInterestedPartyType = (referenceType: ContactReferenceTypeValue): InterestedPartyType =>
  referenceType === ContactReferenceType.Entity ||
  referenceType === ContactReferenceType.Organization
    ? INTERESTED_PARTY_TYPE.Entity
    : INTERESTED_PARTY_TYPE.Individual;

const toConfidenceOption = (value: string | null): DemandConfidenceOption | '' => {
  if (!value) return '';
  if ((DEMAND_CONFIDENCE_OPTIONS as readonly string[]).includes(value)) {
    return value as DemandConfidenceOption;
  }
  if (value === 'Low' || value === 'Medium') return 'Moderate';
  return '';
};

const FieldLabel = ({
  label,
  required,
  info,
}: {
  label: string;
  required?: boolean;
  info?: string;
}) => (
  <LabelRow>
    <Typography variant="body3" color={theme.colors.text.primary}>
      {label}
      {required && <RequiredMark>*</RequiredMark>}
    </Typography>
    {info && (
      <Tooltip title={info}>
        <InfoIcon width={16} height={16} />
      </Tooltip>
    )}
  </LabelRow>
);

export const DemandEntryModal = () => {
  const { open, handleClose: closeGlobalModal, props } = useGlobalModal('DEMAND_ENTRY_MODAL');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { selectedUserRole } = useSelectedUserConfigSlice();
  const isAdmin = selectedUserRole === 'admin';
  const canSetConfidence = isAdmin;
  const canManageDemand = useCan(Permission.DealAdminManage);

  const requestedMode = props?.mode ?? 'add';
  const entry = props?.entry;
  const initialCompany = props?.initialCompany;
  const [viewEditSession, setViewEditSession] = useState<{
    entryId: string;
    locationKey: string;
  } | null>(null);
  const previousViewIdentity = useRef({ mode: requestedMode, entryId: entry?.id });
  if (
    previousViewIdentity.current.mode !== requestedMode ||
    previousViewIdentity.current.entryId !== entry?.id
  ) {
    setViewEditSession(null);
    previousViewIdentity.current = { mode: requestedMode, entryId: entry?.id };
  }
  const handleClose = () => {
    setViewEditSession(null);
    closeGlobalModal();
  };

  const [selectedCompany, setSelectedCompany] = useState<SearchSelectOption | null>(null);
  const [selectedContact, setSelectedContact] = useState<SearchSelectOption | null>(null);
  const [selectedIntroducers, setSelectedIntroducers] = useState<SearchSelectOption[]>([]);
  const [companyQuery, setCompanyQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [introducerQuery, setIntroducerQuery] = useState('');
  const [amountK, setAmountK] = useState('');
  const [confidence, setConfidence] = useState<DemandConfidenceOption | ''>('');
  const [targetValuationM, setTargetValuationM] = useState('');
  const [notes, setNotes] = useState('');
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [createdContact, setCreatedContact] = useState<CreatedContact | null>(null);

  const companyId = selectedCompany?.value ?? '';
  const interestedPartyId = selectedContact?.value ?? '';

  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useQuery({
    ..._getDemandEntry(entry?.id ?? ''),
    enabled: open && !!entry?.id && requestedMode !== 'add',
  });

  const sessionMatches =
    requestedMode === 'view' &&
    !!entry?.id &&
    viewEditSession?.entryId === entry.id &&
    viewEditSession.locationKey === location.key;
  const isRemoved =
    entry?.status === 'Removed' ||
    !!entry?.removedAt ||
    detail?.status === 'Removed' ||
    !!detail?.removedAt;
  const canEditView =
    canManageDemand && !isRemoved && !isDetailLoading && !isDetailError && !!detail;
  const isViewEdit =
    props?.allowViewEdit === true && sessionMatches && canManageDemand && !isRemoved;
  const mode = requestedMode === 'view' ? (isViewEdit ? 'edit' : 'view') : requestedMode;
  const isView = mode === 'view';

  useEffect(() => {
    if (!canManageDemand || isRemoved || location.key !== viewEditSession?.locationKey) {
      setViewEditSession(null);
    }
  }, [canManageDemand, isRemoved, location.key, viewEditSession?.locationKey]);

  useEffect(() => {
    if (!open) return;
    setCreatedContact(null);
    setCompanyQuery('');
    setContactQuery('');
    setIntroducerQuery('');
    if (entry) {
      setSelectedCompany({ value: entry.companyId, label: entry.companyName });
      setSelectedContact({ value: entry.interestedPartyId, label: entry.interestedPartyName });
      setSelectedIntroducers(entry.introducers.map((i) => ({ value: i.id, label: i.name })));
      setAmountK(String(entry.amount / 1000));
      setConfidence(toConfidenceOption(entry.confidenceScore));
      setTargetValuationM(
        entry.targetValuation === null ? '' : String(entry.targetValuation / 1_000_000),
      );
      setNotes(entry.notesPreview ?? '');
    } else {
      setSelectedCompany(
        initialCompany ? { value: initialCompany.id, label: initialCompany.name } : null,
      );
      setSelectedContact(null);
      setSelectedIntroducers([]);
      setAmountK('');
      setConfidence('');
      setTargetValuationM('');
      setNotes('');
    }
  }, [open, entry, initialCompany]);

  useEffect(() => {
    if (open && detail && !isViewEdit) setNotes(detail.notes ?? '');
  }, [open, detail, isViewEdit]);

  const adminCompaniesScroll = useInfiniteScroll(
    {
      ..._getAdminCompaniesInfinite({ search: companyQuery || undefined }),
      enabled: open && isAdmin,
    },
    (page) => page.companies,
  );
  const publicCompaniesScroll = useInfiniteScroll(
    { ..._getCompaniesInfinite({ search: companyQuery || undefined }), enabled: open && !isAdmin },
    (page) => page.companies,
  );
  const {
    items: companies,
    isLoading: isCompaniesLoading,
    isFetchingNextPage: isCompaniesFetchingNext,
    hasNextPage: companiesHasNext,
    fetchNextPage: fetchMoreCompanies,
  } = isAdmin ? adminCompaniesScroll : publicCompaniesScroll;
  const {
    items: introducers,
    isLoading: isIntroducersLoading,
    isFetchingNextPage: isIntroducersFetchingNext,
    hasNextPage: introducersHasNext,
    fetchNextPage: fetchMoreIntroducers,
  } = useInfiniteScroll(
    {
      ..._getAdminIntroducersInfinite({ search: introducerQuery || undefined }),
      enabled: open && isAdmin,
    },
    (page) => page.items,
  );
  const {
    items: contacts,
    isLoading: isContactsLoading,
    isFetchingNextPage: isContactsFetchingNext,
    hasNextPage: contactsHasNext,
    fetchNextPage: fetchMoreContacts,
  } = useInfiniteScroll(
    { ..._getBffContactsInfinite({ search: contactQuery || undefined }), enabled: open },
    (page) => page.contacts,
  );

  const companyOptions = useMemo<SearchSelectOption[]>(
    () => companies.map((c) => ({ value: c.id, label: c.name })),
    [companies],
  );
  const introducerOptions = useMemo<SearchSelectOption[]>(
    () =>
      introducers.map((i) => ({
        value: i.id,
        label: i.name || i.email || i.id,
      })),
    [introducers],
  );
  const contactOptions = useMemo<SearchSelectOption[]>(
    () =>
      contacts.map((c) => ({
        value: c.id,
        label: c.name || c.email || c.id,
        sublabel: c.email ?? undefined,
      })),
    [contacts],
  );
  const contactTypeMap = useMemo(() => {
    const map = new Map<string, InterestedPartyType>();
    if (entry) map.set(entry.interestedPartyId, entry.interestedPartyType);
    contacts.forEach((c) => map.set(c.id, toInterestedPartyType(c.referenceType)));
    if (createdContact) map.set(createdContact.id, createdContact.type);
    return map;
  }, [contacts, entry, createdContact]);

  const confidenceOptions = DEMAND_CONFIDENCE_OPTIONS.map((v) => ({ value: v, label: v }));

  const isValid =
    !!companyId &&
    !!interestedPartyId &&
    Number(amountK) > 0 &&
    (!canSetConfidence || !!confidence) &&
    Number(targetValuationM) > 0;

  const { mutate: createEntry, isPending: isCreating } = useMutation({
    mutationFn: _createDemandEntry,
    onSuccess: onSaved,
    onError: onSaveError,
  });
  const { mutate: createCompanyEntry, isPending: isCreatingCompany } = useMutation({
    mutationFn: _createCompanyDemandEntry,
    onSuccess: onSaved,
    onError: onSaveError,
  });
  const { mutate: updateEntry, isPending: isUpdating } = useMutation({
    mutationFn: _updateDemandEntry,
    onSuccess: onSaved,
    onError: onSaveError,
  });
  const isPending = isCreating || isCreatingCompany || isUpdating;

  function onSaved() {
    showToast('Demand entry saved', 'success');
    void invalidateDemandMutationViews(queryClient);
    handleClose();
  }

  function onSaveError() {
    showToast('Could not save demand entry', 'error');
  }

  const handleViewEdit = () => {
    if (!canEditView || !entry?.id) return;
    setViewEditSession({ entryId: entry.id, locationKey: location.key });
  };

  const handleSubmit = () => {
    if (!isValid || isView || (mode === 'edit' && isDetailLoading)) {
      return;
    }
    const interestedPartyType =
      contactTypeMap.get(interestedPartyId) ??
      entry?.interestedPartyType ??
      INTERESTED_PARTY_TYPE.Individual;

    const companyName = selectedCompany?.label ?? entry?.companyName ?? '';
    const interestedPartyName = selectedContact?.label ?? entry?.interestedPartyName ?? '';
    const introducers = selectedIntroducers.map((o) => ({ id: o.value, name: o.label }));

    const body: DemandEntryPayload = {
      companyId,
      companyName,
      interestedPartyId,
      interestedPartyType,
      interestedPartyName,
      amount: Math.round(Number(amountK) * 1000),
      targetValuation: Math.round(Number(targetValuationM) * 1_000_000),
      introducers,
      notes: notes.trim() || undefined,
      ...(canSetConfidence && confidence
        ? { confidenceScore: confidence as DemandConfidenceOption }
        : {}),
    };

    if (mode === 'edit' && entry) {
      if (
        requestedMode === 'view' &&
        (!canManageDemand || isRemoved || isDetailLoading || isDetailError || !detail)
      ) {
        return;
      }
      updateEntry({ id: entry.id, body });
      return;
    }

    if (isAdmin) {
      createEntry(body);
    } else {
      createCompanyEntry({
        companyId,
        body: {
          interestedPartyId,
          interestedPartyType,
          interestedPartyName,
          amount: Math.round(Number(amountK) * 1000),
          targetValuation: Math.round(Number(targetValuationM) * 1_000_000),
          introducers,
          notes: notes.trim() || undefined,
        },
      });
    }
  };

  const goCreateCompany = () => {
    handleClose();
    navigate(PagePaths.dealsCreateCompany);
  };

  const handleContactCreated = useCreatedContact(
    (contact) => ({
      id: contact.id,
      name: contact.name ?? '',
      type: toInterestedPartyType(contact.referenceType),
    }),
    (contact: CreatedContact) => {
      setCreatedContact(contact);
      setSelectedContact({ value: contact.id, label: contact.name });
      setAddContactOpen(false);
    },
  );

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(next) => (!next ? handleClose() : undefined)}
        title={TITLES[mode]}
        showClose
        maxHeight="90dvh"
      >
        <FormStack>
          <Separator style={{ backgroundColor: theme.colors.border.default }} />
          {SUBTITLES[mode]?.length > 0 ? (
            <Typography variant="body2" color={theme.colors.white[60]}>
              {SUBTITLES[mode]}
            </Typography>
          ) : null}

          {isView && (detail?.removedAt || entry?.removedAt) ? (
            <RemovedBanner>
              <AlertCircleRedIcon width={16} height={16} />
              <Typography variant="body2" color={theme.colors.text.error}>
                This entry was removed on{' '}
                {formatISOAsDayMonYearDashes(detail?.removedAt ?? entry?.removedAt ?? '')}.
              </Typography>
            </RemovedBanner>
          ) : null}

          <Field>
            <FieldLabel label="Company" required />
            <SearchSelect
              options={companyOptions}
              value={selectedCompany}
              onChange={setSelectedCompany}
              onSearchChange={setCompanyQuery}
              loading={isCompaniesLoading}
              loadingMore={isCompaniesFetchingNext}
              hasMore={companiesHasNext}
              onLoadMore={fetchMoreCompanies}
              placeholder="Search company..."
              disabled={isView}
            />
            {!isView && (
              <CreateLink type="button" onClick={goCreateCompany}>
                ＋ Create New Company
              </CreateLink>
            )}
          </Field>

          <Field>
            <FieldLabel label="Interested Party" required />
            <SearchSelect
              key={createdContact?.id ?? 'contact'}
              options={contactOptions}
              value={selectedContact}
              onChange={setSelectedContact}
              onSearchChange={setContactQuery}
              loading={isContactsLoading}
              loadingMore={isContactsFetchingNext}
              hasMore={contactsHasNext}
              onLoadMore={fetchMoreContacts}
              placeholder="Search by name or email..."
              disabled={isView}
              emptyState={
                isView ? undefined : (
                  <ContactEmpty>
                    <Typography variant="body3" color={theme.colors.text.tertiary}>
                      No contacts found matching.
                    </Typography>
                    <Button
                      color="primary"
                      variant="solid"
                      size="sm"
                      onClick={() => setAddContactOpen(true)}
                    >
                      ＋ Create New Contact
                    </Button>
                  </ContactEmpty>
                )
              }
            />
          </Field>

          <Field>
            <FieldLabel label="Ticket Size (k$)" required />
            <SuffixField>
              <Input
                inputMode="decimal"
                placeholder="e.g. 12.5"
                value={amountK}
                onChange={(e) => setAmountK(onlyNumbers(e.target.value))}
                disabled={isView}
              />
              <SuffixText>k$</SuffixText>
            </SuffixField>
          </Field>

          {canSetConfidence && (
            <Field>
              <FieldLabel label="Confidence Score" required />
              <SelectRaisedBg>
                <Select
                  options={confidenceOptions}
                  value={confidence || null}
                  onChange={(v) => setConfidence(v as DemandConfidenceOption)}
                  placeholder="Select confidence score"
                  disabled={isView}
                />
              </SelectRaisedBg>
            </Field>
          )}

          <Field>
            <FieldLabel label="Target Valuation" required />
            <SuffixField>
              <Input
                inputMode="decimal"
                placeholder="e.g. 125"
                value={targetValuationM}
                onChange={(e) => setTargetValuationM(onlyNumbers(e.target.value))}
                disabled={isView}
              />
              <SuffixText>Million USD</SuffixText>
            </SuffixField>
          </Field>

          {isAdmin && (
            <Field>
              <FieldLabel label="Introducers" />
              <SearchSelect
                multiple
                options={introducerOptions}
                value={selectedIntroducers}
                onChange={setSelectedIntroducers}
                onSearchChange={setIntroducerQuery}
                loading={isIntroducersLoading}
                loadingMore={isIntroducersFetchingNext}
                hasMore={introducersHasNext}
                onLoadMore={fetchMoreIntroducers}
                placeholder="Search introducers..."
                disabled={isView}
              />
            </Field>
          )}

          <Field>
            <FieldLabel
              label="Confidential Notes"
              info="These notes are visible only to Gamma Prime team members."
            />
            <TextArea
              rows={4}
              placeholder="Add internal notes…"
              value={notes}
              maxLength={NOTES_MAX_LENGTH}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isView}
            />
          </Field>

          <Separator style={{ backgroundColor: theme.colors.border.default }} />

          <FooterRow>
            {isView ? (
              <Stack direction="row" justifyContent="space-between" width="100%">
                <Button color="tertiary" variant="outline" size="sm" onClick={handleClose}>
                  Close
                </Button>
                {requestedMode === 'view' &&
                  props?.allowViewEdit === true &&
                  canManageDemand &&
                  !isRemoved && (
                    <Button
                      color="primary"
                      variant="solid"
                      size="sm"
                      onClick={handleViewEdit}
                      disabled={!canEditView}
                    >
                      Edit
                    </Button>
                  )}
              </Stack>
            ) : (
              <Stack justifyContent="space-between" width="100%">
                <Button
                  color="tertiary"
                  variant="outline"
                  size="sm"
                  onClick={handleClose}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="solid"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!isValid || isPending || (mode === 'edit' && isDetailLoading)}
                >
                  {mode === 'edit' ? 'Update Demand Entry' : 'Add Demand Entry'}
                </Button>
              </Stack>
            )}
          </FooterRow>
        </FormStack>
      </Modal>

      <AddContactModal
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        onContactCreated={handleContactCreated}
        startWithManualEntry
        canOpenCreatedContact={false}
      />
    </>
  );
};
